/**
 * LearnerHint — destaca elementos clave de la UI cuando el usuario tiene
 * activado el "Modo aprendiz" (los primeros 7 días tras activarlo).
 *
 * Uso:
 *   <LearnerHint label="Crear petición">
 *     <Button>Nueva petición</Button>
 *   </LearnerHint>
 *
 * Si el usuario tiene `learner_mode_until > ahora`, se renderiza un punto
 * amber pulsante en la esquina superior derecha del hijo, con tooltip.
 * Si no, devuelve el children sin modificar (sin overhead visual).
 *
 * El hook `useLearnerMode()` expone `{ active, daysLeft, activate, deactivate }`
 * para el toggle del Perfil.
 */
import React, { useCallback, useMemo, forwardRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

/** Hook: estado y acciones del modo aprendiz */
export function useLearnerMode() {
  const { user, refreshUser } = useAuth();
  const until = user && user !== false ? user.learner_mode_until : null;

  const { active, daysLeft } = useMemo(() => {
    if (!until) return { active: false, daysLeft: 0 };
    const u = new Date(until);
    const now = new Date();
    if (u <= now) return { active: false, daysLeft: 0 };
    const ms = u - now;
    return { active: true, daysLeft: Math.ceil(ms / (1000 * 60 * 60 * 24)) };
  }, [until]);

  const activate = useCallback(async () => {
    await api.post("/auth/learner-mode/activate");
    await refreshUser();
  }, [refreshUser]);

  const deactivate = useCallback(async () => {
    await api.post("/auth/learner-mode/deactivate");
    await refreshUser();
  }, [refreshUser]);

  return { active, daysLeft, until, activate, deactivate };
}

/**
 * Wrapper que añade un dot pulsante amber al hijo cuando el modo aprendiz está activo.
 *
 * IMPORTANTE: este componente DEBE reenviar todas las props/ref que reciba
 * (vía forwardRef y ...rest), porque a menudo se usa dentro de
 * <DialogTrigger asChild>, <PopoverTrigger asChild>, etc., y Radix UI inyecta
 * onClick, onKeyDown, aria-* y ref. Si los swallow-eamos, el botón no abre
 * el dialog/popover (bug reportado 2026-02-25 en Peticiones).
 */
const LearnerHint = forwardRef(function LearnerHint(
  { children, label, testid, ...rest },
  ref,
) {
  const { active } = useLearnerMode();

  if (!active) {
    // Sin overhead visual: clonamos el children y le pasamos las props/ref que
    // Radix nos ha inyectado, en lugar de envolverlo en un span.
    const onlyChild = React.Children.only(children);
    return React.cloneElement(onlyChild, { ...rest, ref });
  }

  return (
    <span
      ref={ref}
      className="relative inline-block"
      data-learner-hint={label || true}
      data-testid={testid ? `learner-hint-${testid}` : undefined}
      {...rest}
    >
      {children}
      {/* Dot pulsante */}
      <span
        className="pointer-events-none absolute -top-1 -right-1 z-10"
        title={label ? `💡 ${label}` : "💡 Modo aprendiz"}
        aria-hidden="true"
      >
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500 ring-2 ring-white shadow"></span>
        </span>
      </span>
    </span>
  );
});

export default LearnerHint;
